import React from "react";
import PropTypes from "prop-types";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";

function SidenavCollapse({ color, icon, name, active, expanded, onClick, hasChildren, isChild, parentActive }) {
  const highlighted = active || parentActive;

  return (
    <ListItem component="li" onClick={onClick} disablePadding sx={{ px: 1.1, py: 0.28 }}>
      <SoftBox
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          width: "100%",
          minHeight: isChild ? 38 : 44,
          py: isChild ? 0.55 : 0.75,
          px: isChild ? 1.4 : 1.5,
          borderRadius: "12px",
          cursor: "pointer",
          transition: "all .18s ease",
          backgroundColor: active ? "#0284C7" : parentActive ? "#F0F9FF" : "transparent",
          color: active ? "#fff" : highlighted ? "#0369A1" : "#475569",
          boxShadow: active ? "0 8px 20px rgba(2,132,199,.20)" : "none",
          "&:hover": {
            backgroundColor: active ? "#0284C7" : "#F0F9FF",
            color: active ? "#fff" : "#0284C7",
            transform: "translateX(2px)",
            "& .MuiListItemIcon-root": { color: active ? "#fff" : "#0284C7" },
          },
          ...(active && {
            "&:before": {
              content: '""',
              position: "absolute",
              right: -8,
              top: 10,
              bottom: 10,
              width: 3,
              borderRadius: 3,
              backgroundColor: "#7DD3FC",
            },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: isChild ? 28 : 36,
            width: isChild ? 28 : 32,
            height: isChild ? 28 : 32,
            borderRadius: isChild ? 8 : 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: active ? "#fff" : highlighted ? "#0284C7" : "#64748B",
            backgroundColor: active ? "rgba(255,255,255,.14)" : highlighted ? "#E0F2FE" : "#F8FAFC",
            transition: "all .18s ease",
          }}
        >
          {typeof icon === "string" ? (
            <Icon sx={{ fontSize: isChild ? "0.55rem" : "1.1rem" }}>{icon}</Icon>
          ) : (
            <SoftBox sx={{ display: "flex", "& svg": { fontSize: isChild ? "0.55rem" : "1.1rem" } }}>{icon}</SoftBox>
          )}
        </ListItemIcon>

        <ListItemText
          primary={name}
          sx={{
            mx: 1,
            "& .MuiTypography-root": {
              fontSize: isChild ? "0.78rem" : "0.83rem",
              fontWeight: highlighted ? 800 : 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />

        {hasChildren && (
          <Icon
            sx={{
              fontSize: "1rem",
              transition: "transform .2s ease",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              color: active ? "rgba(255,255,255,.8)" : highlighted ? "#0284C7" : "#94A3B8",
            }}
          >
            chevron_right
          </Icon>
        )}
      </SoftBox>
    </ListItem>
  );
}

SidenavCollapse.defaultProps = {
  color: "info",
  active: false,
  expanded: false,
  onClick: () => {},
  hasChildren: false,
  isChild: false,
  parentActive: false,
};

SidenavCollapse.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  expanded: PropTypes.bool,
  onClick: PropTypes.func,
  hasChildren: PropTypes.bool,
  isChild: PropTypes.bool,
  parentActive: PropTypes.bool,
};

export default SidenavCollapse;
